/**
 * Package Autocomplete - Live search with fuzzy matching
 * Inline autocomplete for adding packages to templates
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Package, Loader2 } from 'lucide-react';

interface PackageInfo {
  name: string;
  version: string;
  summary: string | null;
  source: 'conda-forge' | 'pypi' | 'local';
  python_requires: string | null;
  category: string | null;
  gpu: boolean | null;
}

interface PackageAutocompleteProps {
  onSelect: (packageName: string) => void;
  existingPackages: string[];
  placeholder?: string;
  autoFocus?: boolean;
}

const API_BASE = '/api/v1/packages';

export default function PackageAutocomplete({
  onSelect,
  existingPackages,
  placeholder = 'Search packages...',
  autoFocus = false
}: PackageAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PackageInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const queryRef = useRef(query);

  // Keep queryRef in sync
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  // Search packages with debouncing and validation
  const searchPackages = useCallback(async (searchQuery: string) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    abortControllerRef.current = new AbortController();

    try {
      // Use validate=true to get real sources and versions
      const response = await fetch(
        `${API_BASE}/search?q=${encodeURIComponent(searchQuery)}&limit=8&validate=true`,
        { signal: abortControllerRef.current.signal }
      );

      // Check if query changed while we were fetching
      if (searchQuery !== queryRef.current) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        // Filter out already-added packages
        const filtered = data.results.filter(
          (pkg: PackageInfo) => !existingPackages.includes(pkg.name)
        );
        setResults(filtered);
        setIsOpen(true); // Always open when we have a query, even if no results
        setSelectedIndex(0);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return; // Request was cancelled, ignore
      }
      console.error('Package search failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [existingPackages]);

  // Handle input change with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      setIsLoading(true); // Show loading immediately
      debounceRef.current = setTimeout(() => {
        searchPackages(query);
      }, 250);
    } else {
      setResults([]);
      setIsOpen(false);
      setIsLoading(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchPackages]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    // Calculate max index including custom option
    const hasCustomOption = query.length >= 2 &&
      !results.some(r => r.name.toLowerCase() === query.trim().toLowerCase()) &&
      !existingPackages.some(p => p.toLowerCase() === query.trim().toLowerCase()) &&
      !isLoading;
    const maxIndex = hasCustomOption ? results.length : results.length - 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, maxIndex));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex === results.length && hasCustomOption) {
          // Custom package option selected
          handleAddCustomPackage(query);
        } else if (results[selectedIndex]) {
          handleSelect(results[selectedIndex].name);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setQuery('');
        setValidationError(null);
        break;
    }
  };

  // Handle package selection
  const handleSelect = (packageName: string) => {
    onSelect(packageName);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setValidationError(null);
    inputRef.current?.focus();
  };

  // Validate and add a custom package name (not in curated index)
  const handleAddCustomPackage = async (packageName: string) => {
    const cleanName = packageName.trim().toLowerCase();
    if (!cleanName || existingPackages.includes(cleanName)) return;

    setIsValidating(true);
    setValidationError(null);

    try {
      const response = await fetch(`${API_BASE}/validate/${encodeURIComponent(cleanName)}`);
      if (response.ok) {
        // Package exists - add it
        onSelect(cleanName);
        setQuery('');
        setResults([]);
        setIsOpen(false);
        inputRef.current?.focus();
      } else {
        // Package doesn't exist
        setValidationError(`Package "${cleanName}" not found on conda-forge or PyPI`);
      }
    } catch (error) {
      setValidationError('Failed to validate package. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'conda-forge':
        return (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-green-500/20 text-green-400 rounded font-medium">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
            conda-forge
          </span>
        );
      case 'pypi':
        return (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded font-medium">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
            PyPI
          </span>
        );
      default:
        return (
          <span className="px-1.5 py-0.5 text-xs bg-gray-600/50 text-gray-400 rounded">
            checking...
          </span>
        );
    }
  };

  const getCategoryLabel = (category: string | null) => {
    if (!category) return null;
    const labels: Record<string, string> = {
      'ml_frameworks': 'ML Framework',
      'data_science': 'Data Science',
      'visualization': 'Visualization',
      'gpu_tools': 'GPU Tools',
      'gpu_computing': 'GPU Computing',
      'quant_finance': 'Quant Finance',
      'cloud_storage': 'Cloud Storage',
      'web_frameworks': 'Web Framework',
      'utilities': 'Utility'
    };
    return labels[category] || category.replace('_', ' ');
  };

  const showDropdown = isOpen && query.length >= 2;

  // Show "add custom" option if query doesn't exactly match any result
  const queryLower = query.trim().toLowerCase();
  const exactMatchExists = results.some(r => r.name.toLowerCase() === queryLower);
  const alreadyAdded = existingPackages.some(p => p.toLowerCase() === queryLower);
  const showAddCustom = query.length >= 2 && !exactMatchExists && !alreadyAdded && !isLoading;

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.length >= 2 && results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="w-full pl-9 pr-8 py-2.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-400 animate-spin" />
        )}
      </div>

      {/* Dropdown Results */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1.5 bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden"
          style={{ maxHeight: '400px' }}
        >
          <div className="overflow-y-auto" style={{ maxHeight: '350px' }}>
            {/* Curated results */}
            {results.map((pkg, index) => (
              <div
                key={pkg.name}
                className={`px-3 py-3 cursor-pointer border-b border-gray-700/50 transition-all duration-150 ${
                  index === selectedIndex
                    ? 'bg-blue-600/20 border-l-2 border-l-blue-500'
                    : 'hover:bg-gray-700/50 border-l-2 border-l-transparent'
                }`}
                onClick={() => handleSelect(pkg.name)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {/* Package Header */}
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="font-semibold text-white">{pkg.name}</span>
                    {pkg.version && (
                      <span className="text-xs text-gray-400 font-mono">v{pkg.version}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {pkg.gpu && (
                      <span className="px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded font-medium">
                        GPU
                      </span>
                    )}
                    {getSourceBadge(pkg.source)}
                  </div>
                </div>

                {/* Package Details */}
                <div className="ml-6 space-y-1">
                  {/* Summary */}
                  {pkg.summary && (
                    <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
                      {pkg.summary}
                    </p>
                  )}

                  {/* Metadata row */}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {pkg.category && (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-gray-500 rounded-full"></span>
                        {getCategoryLabel(pkg.category)}
                      </span>
                    )}
                    {pkg.python_requires && (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-gray-500 rounded-full"></span>
                        Python {pkg.python_requires}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Add custom package option */}
            {showAddCustom && (
              <div
                className={`px-3 py-3 cursor-pointer border-t border-gray-600 transition-all duration-150 ${
                  selectedIndex === results.length
                    ? 'bg-orange-600/20 border-l-2 border-l-orange-500'
                    : 'hover:bg-gray-700/50 border-l-2 border-l-transparent'
                }`}
                onClick={() => handleAddCustomPackage(query)}
                onMouseEnter={() => setSelectedIndex(results.length)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <svg className="h-4 w-4 text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="font-semibold text-white">Add "{query}"</span>
                  </div>
                  <span className="px-1.5 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded font-medium">
                    {isValidating ? 'Checking...' : 'Search repos'}
                  </span>
                </div>
                <p className="ml-6 mt-1 text-xs text-gray-500">
                  Will validate against conda-forge and PyPI
                </p>
              </div>
            )}

            {/* Validation error */}
            {validationError && (
              <div className="px-3 py-3 bg-red-900/20 border-t border-red-800/50">
                <div className="flex items-center gap-2 text-red-400">
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm">{validationError}</span>
                </div>
              </div>
            )}

            {/* Empty state when no results and no custom option */}
            {results.length === 0 && !showAddCustom && !isLoading && (
              <div className="px-4 py-6 text-center">
                <Package className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No packages found</p>
              </div>
            )}

            {/* Loading state */}
            {isLoading && results.length === 0 && (
              <div className="px-4 py-6 text-center">
                <Loader2 className="h-6 w-6 text-blue-400 mx-auto mb-2 animate-spin" />
                <p className="text-sm text-gray-400">Searching packages...</p>
              </div>
            )}
          </div>

          {/* Footer hint */}
          {(results.length > 0 || showAddCustom) && (
            <div className="px-3 py-2 bg-gray-900/50 border-t border-gray-700 flex items-center justify-between text-xs text-gray-500">
              <span>
                <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-400 font-mono">↑↓</kbd>
                <span className="ml-1">navigate</span>
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-400 font-mono">Enter</kbd>
                <span className="ml-1">add package</span>
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-400 font-mono">Esc</kbd>
                <span className="ml-1">close</span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
